import { useCallback, useEffect, useState } from 'react';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isFedRamp } from '@atlassian/atl-context';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest, performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { createResource, useResource, type RouteResource } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	RESOURCE_TYPE_ISSUE_TRANSITION_OPT_IN,
	ISSUE_TRANSITION_LABS_VISIBILITY_API,
	ISSUE_TRANSITION_OPT_IN_STATUS_RESOURCE_KEY,
} from './durationInWeeksDefault.constants.tsx';
import getDefaultIssueTransitionLabsOptInState from './getDefaultIssueTransitionLabsOptInState.tsx';
import type {
	IssueTransitionOptInStatusResourceData,
	IssueTransitionOptInStatusGetResponse,
	IssueTransitionOptInStatusUpdateRequest,
	ResponseStatus,
	UseIssueTransitionLabsOptIn,
} from './IssueTransitionOptInStatusUpdateRequest.types.tsx';

/*
 * API Contract: https://hello.atlassian.net/wiki/spaces/JIE/pages/2983777277/Issue+Transition+Jira+Labs+Toggle+for+Legacy+Experience+Backend+API#API-Contract
 */
const fetchGetIssueTransitionOptInState = async () =>
	performGetRequest<IssueTransitionOptInStatusGetResponse>(ISSUE_TRANSITION_LABS_VISIBILITY_API);

export const issueTransitionOptInStatusResource: RouteResource<IssueTransitionOptInStatusResourceData> =
	createResource<IssueTransitionOptInStatusResourceData>({
		type: RESOURCE_TYPE_ISSUE_TRANSITION_OPT_IN,
		getKey: () => ISSUE_TRANSITION_OPT_IN_STATUS_RESOURCE_KEY,
		getData: async (): Promise<IssueTransitionOptInStatusResourceData> => {
			let issueTransitionOptInStatus = null;

			const isFedRampSrbFallback = fg('aem_migration_jira')
				? !isFeatureEnabled('jira', 'issue-transition-customisation-for-srb')
				: isFedRamp();

			const isModernIssueTransitionSRBCohort =
				fg('issue-transition-customisation-for-srb') || isFedRampSrbFallback;
			if (!fg('render-modern-issue-transition')) {
				return {
					issueTransitionOptInStatus: {
						isEnabled: false,
						expiryTime: -1,
					},
				};
			}

			if (isModernIssueTransitionSRBCohort) {
				return {
					issueTransitionOptInStatus: {
						isEnabled: true,
					},
				};
			}

			try {
				issueTransitionOptInStatus = await fetchGetIssueTransitionOptInState();
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error.statusCode === 404) {
					fireErrorAnalytics({
						error,
						meta: {
							id: 'issueTransitionOptInStatusResource',
							packageName: 'jiraRouterResourcesIssueTransitionLabsOptIn',
							teamName: 'gryffindor',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			}
			return {
				issueTransitionOptInStatus:
					issueTransitionOptInStatus ?? getDefaultIssueTransitionLabsOptInState(),
			};
		},
		maxAge: Number.MAX_VALUE,
		isCritical: true,
	});

const fetchModifyIssueTransitionOptInState = async (
	payload: IssueTransitionOptInStatusUpdateRequest,
) =>
	performPutRequest<IssueTransitionOptInStatusGetResponse>(ISSUE_TRANSITION_LABS_VISIBILITY_API, {
		body: JSON.stringify(payload),
	});

export const useIssueTransitionLabsOptIn: UseIssueTransitionLabsOptIn = () => {
	const {
		data,
		loading: loadingGetResource,
		error: errorGetResource,
		update,
	} = useResource(issueTransitionOptInStatusResource);

	const [issueTransitionLabsOptInState, setIssueTransitionOptInState] =
		useState<IssueTransitionOptInStatusGetResponse>(() => {
			if (data?.issueTransitionOptInStatus) {
				return data.issueTransitionOptInStatus;
			}
			return getDefaultIssueTransitionLabsOptInState();
		});
	const [responseStatusGet, setResponseStatusGet] = useState<ResponseStatus>('not started');
	const [responseStatusPut, setResponseStatusPut] = useState<ResponseStatus>('not started');

	const updateIssueTransitionOptInStatus = useCallback(
		async (updates: IssueTransitionOptInStatusUpdateRequest): Promise<ResponseStatus> => {
			let response: ResponseStatus = 'loading';
			setResponseStatusPut('loading');
			try {
				const updatedPreferences = await fetchModifyIssueTransitionOptInState(updates);
				setResponseStatusPut('success');
				update(() => ({
					issueTransitionOptInStatus: updatedPreferences,
				}));
				response = 'success';
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				fireErrorAnalytics({
					error: err,
					meta: {
						id: 'issueTransitionOptInStatusUpdate',
						packageName: 'jiraRouterResourcesIssueTransitionLabsOptIn',
						teamName: 'gryffindor',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				setResponseStatusPut('error');
				response = 'error';
			}
			return response;
		},
		[update],
	);

	useEffect(() => {
		if (loadingGetResource) {
			// oxlint-disable-next-line react/react-compiler
			setResponseStatusGet('loading');
			return;
		}

		if (errorGetResource) {
			setResponseStatusGet('error');
			return;
		}

		if (data?.issueTransitionOptInStatus) {
			setResponseStatusGet('success');
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setIssueTransitionOptInState(data.issueTransitionOptInStatus);
		}
	}, [loadingGetResource, errorGetResource, data?.issueTransitionOptInStatus]);

	return {
		stateGet: {
			responseStatus: responseStatusGet,
			state: issueTransitionLabsOptInState,
		},
		stateUpdate: {
			responseStatus: responseStatusPut,
			setState: updateIssueTransitionOptInStatus,
		},
	};
};
